import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { callFunction } from "../api/callFunction";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { showToast } from "../utils/showToast";
import { GlobalContext } from "../context/GlobalContext";
import { EnlargedPhotoModal } from "../components/UserGallery";
import { GuestAppearancesList, GuestBudget, BookGuestDialog, AdjustShareDialog, confirmToast, money } from "./GuestAppearances";
import GuestOutreachDialog from "./GuestOutreachDialog";
import OutreachHistoryDialog from "./OutreachHistoryDialog";
import { outreachChip, outreachLine, statsLine, timeAgo } from "./outreachFormat";
import PopularHostAccess from "./PopularHostAccess";
import { PopularListControls, joinRateFloorNote } from "./PopularSortToggle";

const LIST_LIMIT = 30;

// One chip per payouts state, in the order a member moves through them.
function payoutStatus(payouts) {
  // Told about the programme and has not said yes yet — a different thing from
  // never having been asked, now that a member can turn payouts on themselves.
  if (!payouts?.enabled && payouts?.eligible) {
    return { label: "Invited", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300" };
  }
  if (!payouts?.enabled) return { label: "Payouts off", className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" };
  if (payouts.stripeOnboardingComplete) return { label: "Stripe connected", className: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300" };
  if (payouts.stripeAccountId) return { label: "Stripe started", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300" };
  return { label: "Payouts on", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300" };
}

// The selfie check, shown only once it matters: a member nobody has reached
// out to has had no reason to take one.
function identityChip(payouts) {
  if (!payouts?.enabled && !payouts?.eligible) return null;
  switch (payouts.identityStatus) {
    case "verified":
      return {
        label: "Verified",
        className: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
        title: "Their selfie matched the profile photo they have now",
      };
    case "stale":
      return {
        label: "Photo changed",
        className: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
        title: "They changed their profile photo after verifying. They need a new selfie before a fee.",
      };
    case "failed":
      return {
        label: "Selfie failed",
        className: "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300",
        title: "Their last selfie did not match their profile photo",
      };
    default:
      return {
        label: "Not verified",
        className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
        title: "They have not taken the selfie check yet",
      };
  }
}

// How much of this member's draw predates the photo they wear now. Only shown
// when some of it does: a row where every view landed on the current photo has
// nothing to say. Lifetime numbers in every scope, because the question is
// about the member, not the event.
function photoLine(photo) {
  if (!photo?.since || photo.viewersSince == null) return null;
  const total = photo.viewersTotal || 0;
  if (photo.viewersSince >= total) return null;
  const when = timeAgo(photo.since);
  if (!when) return null;
  const approx = photo.sinceSource === "approx";
  return {
    text: `Photo changed ${approx ? "about " : ""}${when} · ${photo.viewersSince} of ${total} viewed since`,
    // Under half of the draw on the current face, with enough of it to mean
    // something, is the thing this line exists to catch.
    warn: total >= 5 && photo.viewersSince < total / 2,
  };
}

// "Can you make it?" history: how often they came after saying yes. Someone
// booked to draw a crowd has to actually turn up.
function reliabilityLine(reliability) {
  if (!reliability?.saidYes) return null;
  return {
    text: `Showed up ${reliability.showed} of the ${reliability.saidYes} ${reliability.saidYes === 1 ? "time" : "times"} they said yes`,
    // Twice or more on record, and absent for half of them or more.
    warn: reliability.saidYes >= 2 && reliability.showed * 2 <= reliability.saidYes,
  };
}

// The ranking answers a different question in each scope, and in each order,
// so the tab says which.
const SCOPES = [
  {
    key: "all",
    label: "All events",
    blurb: "Members across the whole platform, ranked by how many different people opened their profile from an event's RSVP list. This is the list to book from — an upcoming event has no attendees yet.",
    empty: "No profile views recorded yet. Views are counted when a member opens someone's profile from the RSVP list on an event page.",
    rateBlurb: "Members across the whole platform, ranked by join rate: of the people who opened their profile from an event's RSVP list before joining that event, the share who then joined. It shows who helps people decide to come, not just who gets looked at.",
    rateEmpty: "Nobody has enough people looking at them before joining to rank by join rate yet.",
  },
  {
    key: "event",
    label: "This event",
    blurb: "Who drew people to THIS event: ranked by how many different people opened their profile from this event's RSVP list. Only knowable after the fact, so use it to learn what worked.",
    empty: "Nobody's profile has been opened from this event's RSVP list yet.",
    rateBlurb: "Who drew people to THIS event, by join rate: of the people who opened their profile on this event's RSVP list before joining, the share who then joined.",
    rateEmpty: "Not enough people have looked at anyone on this event's RSVP list before joining to rank by join rate yet.",
  },
  {
    key: "enrolled",
    label: "Payouts on",
    blurb: "Every member with payouts turned on, wherever they rank. This is where to find someone you enrolled who has since dropped out of the top 30.",
    empty: "Nobody has payouts turned on yet. Switch to All events to find members to enrol.",
    rateBlurb: "Every member with payouts turned on, wherever they rank, in join rate order.",
  },
];

/**
 * Popular guests: members ranked by how many different people opened their
 * profile from an event's RSVP list (or by how many of the ones who looked
 * before joining then joined), and the guests booked to be paid for showing up
 * to THIS event.
 *
 * Two zones on purpose. The booked guests come first because they are live
 * commitments an operator has to act on (confirm, pay, cancel); the ranking
 * below is the discovery surface they act FROM. The ranking is platform-wide —
 * it is a property of the member, not of this event — and says so.
 */
export function PopularGuestsSection({ eventID, eventData }) {
  // Offering and paying are CLAIM-ONLY server-side, matching the host payout
  // screens; everything else on this tab accepts the vendors flag. Gating on the
  // claim here means a flag-only operator is never shown a button that would
  // fail with permission-denied.
  const { globalState } = useContext(GlobalContext);
  const canPay = Boolean(globalState?.hasSuperAdminClaim);

  const [guests, setGuests] = useState([]);
  const [skippedDeleted, setSkippedDeleted] = useState(0);
  // Show only people nobody has reached from this tab: the list to work from
  // when the question is "who is left?". How many it hides rides along.
  const [unmessagedOnly, setUnmessagedOnly] = useState(false);
  const [skippedMessaged, setSkippedMessaged] = useState(0);
  const [historyTarget, setHistoryTarget] = useState(null); // the guest whose messages are open
  const [enlargedPhotoUser, setEnlargedPhotoUser] = useState(null); // the guest whose photo is open
  const [scope, setScope] = useState("all"); // "all" | "event" | "enrolled"
  const [sort, setSort] = useState("viewers"); // "viewers" | "joinRate"
  // The join-rate floor the server used for the rows on screen.
  const [minBeforeJoining, setMinBeforeJoining] = useState(null);
  const [appearances, setAppearances] = useState([]);
  const [appearanceTotals, setAppearanceTotals] = useState(null);
  const [settlement, setSettlement] = useState(null);   // where this night stands: split, locked, can split
  const [listBusy, setListBusy] = useState(null);       // "settle" | "notify"
  const [adjustTarget, setAdjustTarget] = useState(null); // the appearance whose share is being moved
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // userID -> "payouts"; appearanceId -> "confirm" | "pay" | ...
  const [busy, setBusy] = useState({});
  const [outreachTarget, setOutreachTarget] = useState(null); // the guest being messaged
  const [isSendingOutreach, setIsSendingOutreach] = useState(false);
  // Ids we put on the event during this visit. The parent hands us a snapshot of
  // the event doc and has no way to re-read it, so without this a guest we just
  // invited would still be offered the invite tick.
  const [invitedNow, setInvitedNow] = useState(() => new Set());
  const [coupons, setCoupons] = useState(null); // null until first loaded
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponsError, setCouponsError] = useState("");
  const [offerTarget, setOfferTarget] = useState(null); // { guest, existing }
  const [isOffering, setIsOffering] = useState(false);
  const [pool, setPool] = useState(null);          // this event's guest budget
  const [isSavingRate, setIsSavingRate] = useState(false);

  const { vendorID, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters } = eventData || {};
  // Same set as InviteSection / eventAttendeeIds on the server: anyone already
  // attached to this event has no Invite button.
  const onEvent = useMemo(() => new Set([
    vendorID,
    ...(rsvpSitters || []),
    ...(rsvpMovers || []),
    ...(sitters || []),
    ...(movers || []),
    ...(invitedSitters || []),
    ...invitedNow,
  ].filter(Boolean)), [vendorID, rsvpSitters, rsvpMovers, sitters, movers, invitedSitters, invitedNow]);

  // Checked-in is a HINT for the operator confirming attendance, never the thing
  // that authorises money — members can write these arrays themselves.
  const checkedInIds = useMemo(
    () => new Set([...(sitters || []), ...(movers || [])].filter(Boolean)),
    [sitters, movers]
  );

  const appearanceByUser = useMemo(() => {
    const m = new Map();
    appearances.forEach((a) => {
      if (a.status !== "cancelled" && a.status !== "no_show") m.set(a.userID, a);
    });
    return m;
  }, [appearances]);

  const loadAppearances = useCallback(async () => {
    if (!eventID) return;
    const data = await callFunction("adminListGuestAppearances", { eventID }, { timeout: 60000 });
    setAppearances(Array.isArray(data?.appearances) ? data.appearances : []);
    setAppearanceTotals(data?.totals || null);
    setSettlement(data?.settlement || null);
  }, [eventID]);

  // The budget is read separately from the appearances: it also depends on the
  // event's revenue and the host's share, both of which move without anyone
  // touching an appearance. A failure leaves the budget hidden rather than
  // blocking the tab — every offer is still checked server-side.
  const loadPool = useCallback(async () => {
    if (!eventID) return;
    try {
      const data = await callFunction("getEventGuestPool", { eventID }, { timeout: 60000 });
      setPool(data || null);
    } catch (err) {
      console.error("getEventGuestPool failed:", err);
      setPool(null);
    }
  }, [eventID]);

  // Coupons are only ever needed once an operator opens the composer, so they
  // are fetched then rather than on every visit to the tab. Loaded once and kept:
  // a coupon list does not move while someone works through a ranking.
  const loadCoupons = useCallback(async () => {
    if (!eventID || coupons !== null || couponsLoading) return;
    setCouponsLoading(true);
    try {
      const data = await callFunction("adminListCoupons", { eventID }, { timeout: 60000 });
      setCoupons(Array.isArray(data?.coupons) ? data.coupons : []);
    } catch (err) {
      // A missing coupon list must not block the message: the rest of the
      // composer still works, the picker is simply empty. It says so rather
      // than reading as "this event has no coupons" — a flag-only operator is
      // refused this list for someone else's event.
      console.error("adminListCoupons failed:", err);
      setCoupons([]);
      setCouponsError(err?.message || "Could not load this event's coupons.");
    } finally {
      setCouponsLoading(false);
    }
  }, [eventID, coupons, couponsLoading]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [ranking] = await Promise.all([
        callFunction("listPopularGuests", {
          limit: LIST_LIMIT,
          enabledOnly: scope === "enrolled",
          ...(scope === "event" && eventID ? { eventID } : {}),
          // The event this page is open on, whatever the ranking scope: it is
          // what "about this event" means on every row.
          ...(eventID ? { contextEventID: eventID } : {}),
          unmessagedOnly,
          sort,
        }, { timeout: 60000 }),
        loadPool(),
        loadAppearances().catch((err) => {
          // A failed appearance load must not blank the ranking; it is reported
          // on its own line instead.
          console.error("adminListGuestAppearances failed:", err);
          showToast("Could not load this event's paid guests", { type: "error" });
        }),
      ]);
      setGuests(Array.isArray(ranking?.guests) ? ranking.guests : []);
      setSkippedDeleted(ranking?.skippedDeleted || 0);
      setSkippedMessaged(ranking?.skippedMessaged || 0);
      setMinBeforeJoining(ranking?.minBeforeJoining || null);
    } catch (err) {
      console.error("listPopularGuests failed:", err);
      setLoadError(err?.message || "Could not load popular guests.");
    } finally {
      setIsLoading(false);
    }
  }, [loadAppearances, loadPool, scope, sort, eventID, unmessagedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const patchGuest = (userID, patch) =>
    setGuests((prev) => prev.map((g) => (g.userID === userID ? { ...g, ...patch } : g)));

  const setBusyFor = (key, action) =>
    setBusy((prev) => {
      const next = { ...prev };
      if (action) next[key] = action;
      else delete next[key];
      return next;
    });

  // One way only: OFF. Joining is the member's own act — "Reach out" with the
  // earn nudge sets `eligible`, and the button on /earn enrols them and walks
  // them into Stripe in the same tap. Switching someone on from here produced a
  // member who never said yes, with no Stripe account to pay into, parked on
  // "Payouts on" forever. This is the withdrawal: the server clears `eligible`
  // with it, so an old nudge cannot be used to rejoin.
  const disablePayouts = async (guest, { force = false } = {}) => {
    if (!force && !window.confirm(`Turn off payouts for ${guest.name || "this member"}? The setup card leaves their profile. Their Stripe account is kept, but they can only rejoin from a new earn nudge.`)) {
      return;
    }
    setBusyFor(guest.userID, "payouts");
    try {
      const data = await callFunction("setGuestPayoutsEnabled", { userID: guest.userID, enabled: false, ...(force ? { force: true } : {}) });
      patchGuest(guest.userID, { payouts: data.payouts });
      showToast(`Payouts off for ${guest.name || "member"}`, { type: "success" });
      await loadAppearances().catch(() => {});
    } catch (err) {
      console.error("setGuestPayoutsEnabled failed:", err);
      // The server refuses to strand money it already promised. Surface the
      // amount and let the operator decide, rather than making them guess.
      if (!force && err?.code === "functions/failed-precondition") {
        if (window.confirm(`${err.message}\n\nTurn payouts off anyway? Those fees stay unpaid unless they join the programme again.`)) {
          setBusyFor(guest.userID, null);
          await disablePayouts(guest, { force: true });
          return;
        }
      } else {
        showToast(err?.message || "Could not update payouts", { type: "error" });
      }
    } finally {
      setBusyFor(guest.userID, null);
    }
  };

  const openOutreach = (guest) => {
    setOutreachTarget(guest);
    loadCoupons();
  };

  /**
   * One message: the earn nudge, the event invite, a coupon, a note — whichever
   * the operator ticked, assembled and sent server-side as a single text, plus a
   * single push when the message is a plain invite (an earn nudge is text only —
   * see GuestOutreachDialog).
   */
  const sendOutreach = async ({ message, includeEarn, inviteToEvent, couponCode }) => {
    const guest = outreachTarget;
    if (!guest) return;
    setIsSendingOutreach(true);
    try {
      const data = await callFunction(
        "sendGuestPayoutInvite",
        {
          userID: guest.userID,
          ...(eventID ? { eventID } : {}),
          ...(message ? { message } : {}),
          ...(couponCode ? { couponCode } : {}),
          includeEarn,
          inviteToEvent,
        },
        { timeout: 60000 }
      );
      // Only the earn nudge stamps the member's payouts doc (eligibility and
      // the cooldown); the outreach log below covers every send.
      if (includeEarn) {
        patchGuest(guest.userID, {
          payouts: {
            ...guest.payouts,
            eligible: true,
            invitedAt: new Date().toISOString(),
            inviteCount: (guest.payouts?.inviteCount || 0) + 1,
          },
        });
      }
      // The server hands the log back as it stands, so the row reads
      // "messaged just now" without a reload.
      if (data?.outreach) patchGuest(guest.userID, { outreach: data.outreach });
      showToast(
        data?.message ? `${guest.name || "Member"}: ${data.message}` : "Sent",
        { type: data?.success ? "success" : "warning" }
      );
      setOutreachTarget(null);
      if (data?.invitedToEvent === "yes") {
        setInvitedNow((prev) => new Set(prev).add(guest.userID));
      }
    } catch (err) {
      console.error("sendGuestPayoutInvite failed:", err);
      showToast(err?.message || "Could not send", { type: "error" });
    } finally {
      setIsSendingOutreach(false);
    }
  };

  const submitBooking = async ({ note, notify }) => {
    if (!offerTarget) return;
    setIsOffering(true);
    try {
      const data = await callFunction(
        "adminOfferGuestAppearance",
        {
          userID: offerTarget.guest.userID,
          eventID,
          ...(note ? { note } : {}),
          notify,
        },
        { timeout: 60000 }
      );
      if (data?.pool) setPool(data.pool);
      if (data?.outreach) patchGuest(offerTarget.guest.userID, { outreach: data.outreach });
      await loadAppearances();
      setOfferTarget(null);
      const who = offerTarget.guest.name || "member";
      showToast(notify ? `Booked ${who}, ${data?.notifyMessage || "texted"}` : `Booked ${who}`, { type: "success" });
    } catch (err) {
      console.error("adminOfferGuestAppearance failed:", err);
      showToast(err?.message || "Could not book them", { type: "error" });
    } finally {
      setIsOffering(false);
    }
  };

  // Split the night's pool among everyone confirmed. Re-running is how a late
  // confirmation gets included, and it clears any adjustments, so it asks.
  const settleShares = async () => {
    if (settlement?.settled || settlement?.needsResettle) {
      if (!window.confirm("Split this night again? Every share goes back to an equal split and any adjustments are cleared.")) return;
    }
    setListBusy("settle");
    try {
      const data = await callFunction("adminSettleGuestAppearances", { eventID }, { timeout: 60000 });
      if (data?.pool) setPool(data.pool);
      await loadAppearances();
      const split = data?.split || {};
      showToast(
        split.belowMinimum
          ? `Split ${money(split.poolCents)} among ${split.guestCount}: under the minimum share, so nobody is paid`
          : `Split ${money(split.poolCents)} among ${split.guestCount}: ${money(split.equalShareCents)} each`,
        { type: "success" }
      );
    } catch (err) {
      console.error("adminSettleGuestAppearances failed:", err);
      showToast(err?.message || "Could not work out shares", { type: "error" });
      await loadAppearances().catch(() => {});
    } finally {
      setListBusy(null);
    }
  };

  const notifyShares = async () => {
    setListBusy("notify");
    try {
      const data = await callFunction("adminNotifyGuestShares", { eventID }, { timeout: 120000 });
      await loadAppearances();
      const bits = [`Texted ${data?.sent || 0}`];
      if (data?.unreachable) bits.push(`${data.unreachable} could not be reached`);
      showToast(bits.join(", "), { type: data?.unreachable ? "warning" : "success" });
    } catch (err) {
      console.error("adminNotifyGuestShares failed:", err);
      showToast(err?.message || "Could not text the shares", { type: "error" });
    } finally {
      setListBusy(null);
    }
  };

  const submitAdjust = async ({ amountCents, allowOverPool = false }) => {
    if (!adjustTarget) return;
    setIsAdjusting(true);
    try {
      await callFunction(
        "adminSetGuestShare",
        { appearanceId: adjustTarget.appearanceId, amountCents, ...(allowOverPool ? { allowOverPool: true } : {}) },
        { timeout: 60000 }
      );
      await Promise.all([loadAppearances(), loadPool()]);
      setAdjustTarget(null);
      showToast(`${adjustTarget.name || "Member"} now gets ${money(amountCents)}`, { type: "success" });
    } catch (err) {
      console.error("adminSetGuestShare failed:", err);
      // Over the pool is a decision, not a failure: the server does the
      // arithmetic and refuses once, the operator sees the numbers and chooses.
      if (!allowOverPool && err?.code === "functions/failed-precondition" && /guest pool/i.test(err?.message || "")) {
        setIsAdjusting(false);
        if (window.confirm(`${err.message}\n\nAdjust it anyway?`)) {
          await submitAdjust({ amountCents, allowOverPool: true });
        }
        return;
      }
      showToast(err?.message || "Could not adjust the share", { type: "error" });
    } finally {
      setIsAdjusting(false);
    }
  };

  const setEventShareRate = async (rate) => {
    setIsSavingRate(true);
    try {
      const data = await callFunction("adminSetEventGuestShareRate", { eventID, rate }, { timeout: 60000 });
      if (data?.pool) setPool(data.pool);
      showToast(
        rate === null ? "This event follows the platform share again" : `This event now shares ${Math.round(rate * 100)}%`,
        { type: "success" }
      );
    } catch (err) {
      console.error("adminSetEventGuestShareRate failed:", err);
      showToast(err?.message || "Could not change the share", { type: "error" });
    } finally {
      setIsSavingRate(false);
    }
  };

  const handleAppearanceAction = async (appearance, action) => {
    if (action === "adjust") {
      setAdjustTarget(appearance);
      return;
    }
    const confirmCopy = {
      // Paying is the one irreversible step here, so it is the one that asks.
      pay: `Send ${money(appearance.amountCents)} to ${appearance.name || "this member"} now? This moves real money and can't be undone.`,
      cancel: `Withdraw the ${money(appearance.amountCents)} offer to ${appearance.name || "this member"}?`,
      no_show: `Mark ${appearance.name || "this member"} as a no-show? They won't be paid.`,
    }[action];
    if (confirmCopy && !window.confirm(confirmCopy)) return;

    setBusyFor(appearance.appearanceId, action);
    try {
      if (action === "pay") {
        const data = await callFunction("adminPayGuestAppearance", { appearanceId: appearance.appearanceId }, { timeout: 120000 });
        showToast(`Sent ${money(data?.amountCents || appearance.amountCents)} to ${appearance.name || "member"}`, { type: "success" });
      } else {
        await callFunction("adminUpdateGuestAppearance", { appearanceId: appearance.appearanceId, action }, { timeout: 60000 });
        showToast(
          action === "confirm"
            ? confirmToast(settlement)
            : action === "no_show" ? "Marked as a no-show" : "Booking withdrawn",
          { type: "success" }
        );
      }
      await loadAppearances();
    } catch (err) {
      console.error(`adminGuestAppearance ${action} failed:`, err);
      showToast(err?.message || "Could not update", { type: "error" });
      // Re-read either way: a rejected pay may still have changed state
      // (a claim taken by the sweep, say), and a stale row is worse than a slow one.
      await loadAppearances().catch(() => {});
    } finally {
      setBusyFor(appearance.appearanceId, null);
    }
  };

  const activeScope = SCOPES.find((s) => s.key === scope) || SCOPES[0];
  const byRate = sort === "joinRate";
  const floorNote = byRate && !isLoading ? joinRateFloorNote(scope, minBeforeJoining) : null;

  return (
    <div>
      {/* Which hosts get this tab on their own events. Draws nothing without
          the claim: granting is claim-only server-side. */}
      <PopularHostAccess />

      <GuestBudget
        pool={pool}
        settlement={settlement}
        canEdit={canPay}
        isSaving={isSavingRate}
        onSetRate={setEventShareRate}
      />

      <GuestAppearancesList
        appearances={appearances}
        totals={appearanceTotals}
        settlement={settlement}
        checkedInIds={checkedInIds}
        busyById={busy}
        listBusy={listBusy}
        canPay={canPay}
        autoPay={pool ? pool.autoPay : null}
        onAction={handleAppearanceAction}
        onSettle={settleShares}
        onNotify={notifyShares}
        onExpandPhoto={setEnlargedPhotoUser}
      />

      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {byRate ? activeScope.rateBlurb : activeScope.blurb}
        </p>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="text-sm underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0 disabled:no-underline disabled:cursor-not-allowed"
        >
          {isLoading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-3">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setScope(s.key)}
            aria-pressed={scope === s.key}
            className={`flex-1 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${scope === s.key
              ? "bg-white dark:bg-gray-900 shadow text-gray-900 dark:text-gray-100"
              : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <PopularListControls
        sort={sort}
        onSortChange={setSort}
        unmessagedOnly={unmessagedOnly}
        onUnmessagedChange={setUnmessagedOnly}
        skippedMessaged={skippedMessaged}
        isLoading={isLoading}
      />
      {floorNote && <p className="-mt-1 mb-3 text-xs text-gray-500 dark:text-gray-400">{floorNote}</p>}

      {isLoading && (
        <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400 text-sm">
          Loading popular guests...
        </div>
      )}

      {!isLoading && skippedDeleted > 0 && (
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          {skippedDeleted} deleted {skippedDeleted === 1 ? "account was" : "accounts were"} skipped.
        </p>
      )}

      {!isLoading && loadError && (
        <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 text-sm">
          {loadError}
        </div>
      )}

      {!isLoading && !loadError && guests.length === 0 && (
        <div className="p-6 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">{(byRate && activeScope.rateEmpty) || activeScope.empty}</p>
        </div>
      )}

      {/* The inner scroller earns its keep on a desktop, where the booked guests
          and the budget stay in view above it. On a phone it would leave a
          thumb-sized window onto a 30-row list, so there the page scrolls. */}
      <div className="flex flex-col gap-2 sm:max-h-[calc(100dvh-300px)] sm:overflow-y-auto">
        {(isLoading ? [] : guests).map((guest, index) => {
          const status = payoutStatus(guest.payouts);
          const enabled = Boolean(guest.payouts?.enabled);
          const action = busy[guest.userID];
          const booked = appearanceByUser.get(guest.userID);
          const identity = identityChip(guest.payouts);
          const photo = photoLine(guest.photo);
          const contact = outreachChip(guest.outreach);
          const contactLine = outreachLine(guest.outreach, eventID);
          // Offering money is claim-only server-side, needs somewhere to send
          // it, and is refused unless the selfie check is current, so the
          // button only appears once all three hold. The line under the stats
          // says why when it does not.
          const identityCurrent = guest.payouts?.identityStatus === "verified";
          const canOffer = canPay && enabled && identityCurrent;
          return (
            <div
              key={guest.userID}
              className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="w-6 text-right text-sm text-gray-400 shrink-0">{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => setEnlargedPhotoUser(guest)}
                    title="View photo"
                    className="shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {guest.photoURL ? (
                      <img
                        src={variantURL(guest.photoURL, 200)}
                        onError={fallbackToOriginal(guest.photoURL)}
                        alt={guest.name || "Member"}
                        loading="lazy"
                        className="w-12 h-12 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
                        <span className="text-gray-500 dark:text-gray-400 text-lg">
                          {(guest.name || "?").charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                        {guest.name || "Unnamed member"}
                      </span>
                      {guest.isHost && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                          Host
                        </span>
                      )}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${status.className}`}>
                        {status.label}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${contact.className}`}>
                        {contact.label}
                      </span>
                      {booked && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                          {booked.settled ? `Booked · ${money(booked.amountCents)}` : "Booked"}
                        </span>
                      )}
                      {identity && (
                        <span
                          title={identity.title}
                          className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${identity.className}`}
                        >
                          {identity.label}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{statsLine(guest.stats, scope, sort)}</div>
                    {reliabilityLine(guest.reliability) && (
                      <div
                        className={`text-xs mt-0.5 ${reliabilityLine(guest.reliability).warn
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-gray-500 dark:text-gray-400"}`}
                      >
                        {reliabilityLine(guest.reliability).text}
                      </div>
                    )}
                    {photo && (
                      <div className={`text-xs mt-0.5 ${photo.warn ? "text-amber-700 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}`}>
                        {photo.text}
                      </div>
                    )}
                    {canPay && enabled && !identityCurrent && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {guest.payouts?.identityStatus === "stale"
                          ? "Changed their photo after verifying. They need a new selfie before a fee."
                          : "Needs the selfie check before a fee. The earn link takes them there."}
                      </div>
                    )}
                    {contactLine && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{contactLine}</div>
                    )}
                  </div>
                </div>

                {/* Two columns on a phone, so every button is thumb-sized and
                    nothing sits in a 4-across row squeezed to its text. */}
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end shrink-0">
                  {/* Hidden rather than disabled: a flag-only operator can never
                      book, payouts have to be on before there is an account to
                      pay into, and the server refuses a booking for a member
                      whose selfie check is not current. Already booked is
                      handled on their row above. A button that can never be
                      pressed is just a thing to wonder about. */}
                  {canOffer && !booked && (
                    <button
                      type="button"
                      onClick={() => setOfferTarget({ guest })}
                      disabled={Boolean(action)}
                      title="Book them to show up to this event for a share of the night"
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      Book for this event
                    </button>
                  )}
                  {guest.outreach?.count > 0 && (
                    <button
                      type="button"
                      onClick={() => setHistoryTarget(guest)}
                      disabled={Boolean(action)}
                      title="Every message sent to them from this tab"
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      History
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => openOutreach(guest)}
                    disabled={Boolean(action)}
                    title="One message: the earn nudge, an invite to this event, a coupon, or all three"
                    className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    Reach out
                  </button>
                  {/* Only ever a withdrawal, so it appears only once there is
                      something to withdraw. */}
                  {enabled && (
                    <button
                      type="button"
                      onClick={() => disablePayouts(guest)}
                      disabled={Boolean(action)}
                      title="Take them out of the payouts programme"
                      className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                    >
                      {action === "payouts" ? "Saving..." : "Turn off payouts"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {outreachTarget && (
        <GuestOutreachDialog
          guest={outreachTarget}
          eventName={eventData?.name}
          eventID={eventID}
          coupons={coupons}
          couponsLoading={couponsLoading}
          couponsError={couponsError}
          alreadyOnEvent={onEvent.has(outreachTarget.userID)}
          isSending={isSendingOutreach}
          onCancel={() => setOutreachTarget(null)}
          onConfirm={sendOutreach}
        />
      )}

      {historyTarget && (
        <OutreachHistoryDialog
          guest={historyTarget}
          eventID={eventID}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {offerTarget && (
        <BookGuestDialog
          guest={offerTarget.guest}
          eventName={eventData?.name}
          isSaving={isOffering}
          onCancel={() => setOfferTarget(null)}
          onConfirm={submitBooking}
        />
      )}

      {adjustTarget && (
        <AdjustShareDialog
          appearance={adjustTarget}
          pool={pool}
          othersCents={appearances
            .filter((a) => a.appearanceId !== adjustTarget.appearanceId)
            .filter((a) => a.settled && ["confirmed", "processing", "paid", "needs_review"].includes(a.status))
            .reduce((n, a) => n + (a.amountCents || 0), 0)}
          isSaving={isAdjusting}
          onCancel={() => setAdjustTarget(null)}
          onConfirm={submitAdjust}
        />
      )}

      <EnlargedPhotoModal
        user={enlargedPhotoUser}
        onClose={() => setEnlargedPhotoUser(null)}
      />
    </div>
  );
}

export default PopularGuestsSection;
